// Constants shared across the app.

/** Email domain we restrict authentication to. */
export const ALLOWED_EMAIL_DOMAIN = 'onlogic.com';

/**
 * Canonical public base URL of the Progressive Web App. Use it to build the
 * absolute links in anything the app sends OUT — Google Chat notifications,
 * flight emails — where a relative route is not enough.
 *
 * This is the custom domain, deliberately NOT the Firebase default
 * `onlogic-team.web.app`. Both hosts serve the same app, but the .web.app
 * address is the backup one: it is what the app's own environment panel
 * reports as "Staging" (apps/web/src/lib/environment.ts), so a notification
 * linking there sends people to the wrong front door.
 *
 * It lives here, once, because the Chat message builder and the flight mailer
 * each hard-coded their own copy and both drifted from the live domain — which
 * is exactly how every "Open in Team App" link ended up pointing at .web.app.
 * One definition, so a future host change is a single edit.
 */
export const APP_BASE_URL = 'https://team.onlogic.com';

/**
 * Canonical site codes. Single source of truth for the set of OnLogic sites
 * referenced across the app — door audiences, the badge/claims `site` field,
 * office config, and the fleet. Add a new site here once and every consumer
 * (web pickers, the functions-side time-zone fallback) picks it up.
 *
 * These are the 5-character codes that are live in data (e.g. 'USQSB' for
 * South Burlington), NOT the 3-character airport-style codes some older notes
 * used. The matching IANA time zones live in the functions-side
 * SITE_TIME_ZONES fallback map (apps/functions/src/door/policy.ts), which is
 * typed `Record<SiteCode, string>` so it must stay in step with this list.
 */
export const SITE_CODES = ['USQSB', 'USCFC', 'NLOOS', 'TWNTC', 'MYPEN'] as const;
export type SiteCode = (typeof SITE_CODES)[number];

/**
 * Notification categories. Single source of truth shared by the server
 * (sendNotification) and the web client (NotificationsPage filter tabs +
 * record type) so the two can't drift — they did: the client union had
 * dropped 'surveys', 'door_outage', and 'door_security'. Add a category here
 * once and both sides pick it up.
 *
 * - door_outage: the door controller is unavailable (availability problem).
 * - door_security: a security alert such as repeated failed unlocks (IMP-028),
 *   kept distinct from door_outage so the two read differently in the data.
 * - reminders: scheduled nudges (e.g. booking/return, pending approvals) —
 *   the reminders engine (IMP-031/032) routes through this category.
 */
/**
 * Google Chat delivery rollout mode, stored at `config/features.chatDelivery`.
 *   - 'off'    — Chat channel disabled; notifications use FCM only (default).
 *   - 'dryRun' — routing is computed and logged, but NO Chat API call is made.
 *                Safe to deploy before the Workspace app is approved/installed.
 *   - 'on'     — live: deliver via Google Chat per the routing rules.
 * The web Settings "Notification Delivery" toggle is only shown once this is
 * 'on' (see apps/web/src/lib/features.ts).
 */
export const CHAT_DELIVERY_MODES = ['off', 'dryRun', 'on'] as const;
export type ChatDeliveryMode = (typeof CHAT_DELIVERY_MODES)[number];

export const NOTIFICATION_CATEGORIES = [
  'facilities',
  'purchase_request',
  'sla_breach',
  'events',
  'surveys',
  'door_outage',
  'door_security',
  'bookings',
  'reminders',
  'flights',
  'feedback',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

/**
 * Default quiet-hours window (hours on a 24-hour clock, local time) — non-urgent
 * Google Chat messages and push alerts are held back until it ends. Each person
 * can move or turn off their own window in Settings (users/{uid}/settings/
 * notifications.quietHours); these are what applies until they do. It was a
 * fixed 19:00–07:00 for everyone before the window became a setting.
 */
export const QUIET_HOURS_START = 22; // 22:00
export const QUIET_HOURS_END = 7; // 07:00

/**
 * The hours a person may pick. The window has to sit inside 18:00–08:00 so it
 * can never swallow the working day. Every start here is earlier, overnight,
 * than every end, so any pair is a valid window of 4 to 14 hours and the
 * Settings picker never needs an error message. Hourly, like Caliber's.
 */
export const QUIET_HOURS_START_OPTIONS = [18, 19, 20, 21, 22, 23, 0] as const;
export const QUIET_HOURS_END_OPTIONS = [4, 5, 6, 7, 8] as const;

/** Default purchase request approval thresholds (USD). Override in config/purchaseRequests. */
export const DEFAULT_PR_THRESHOLDS = {
  managerOnly: 1_000, // <= $1k: manager only
  managerPlusFinance: 10_000, // <= $10k: manager + Finance approver
  // anything above adds an executive approver
} as const;

/**
 * The standard OnLogic expense classifications offered on a purchase request,
 * as supplied by Finance. This is the FALLBACK list: it is used whenever no
 * cost types are configured at `config/purchaseRequests`, so the module works
 * correctly before an admin touches it and there is no seeding step to forget.
 * Admins override it by adding entries in the Purchase Request settings, which
 * replaces this list wholesale rather than merging with it.
 *
 * Order is Finance's, not alphabetical — the picker sorts for display.
 */
export const DEFAULT_COST_TYPES = [
  'Office Supplies',
  'Meals & Entertainment',
  'Travel',
  'Human Resources',
  'Information Technology',
  'Dues and Subscriptions',
  'Bank Fees',
  'Charitable Contributions',
  'Postage',
  'Professional Development',
  'Rent',
  'Maintenance and Repairs',
  'Utilities',
  'Insurance',
  'Legal',
  'Accounting',
  'Other',
  'Payroll Service',
  'Research & Development',
  'Marketing',
  'COGS - E&O/Scrap',
  'COGS - Inventory Adjustment',
  'Duties/Tariffs',
] as const;

/** Facilities ticket SLA targets in hours. */
export const FACILITIES_SLA_HOURS = {
  urgent: { response: 1, resolution: 8 },
  high: { response: 4, resolution: 24 },
  normal: { response: 24, resolution: 72 },
  low: { response: 48, resolution: 240 },
} as const;
